import React from 'react';
import { Share2, Play } from 'lucide-react';
import { sanitizeText, sanitizeHTML } from '../lib/sanitize';
import { hexToRgba } from '../lib/cardStyle';

// Hero "sobreposto": imagem com altura fixa (limitada) + véu + título/olho-d'água/
// CTAs sobrepostos no rodapé. Estrutura igual ao original do Lovable, com cap de
// altura pra nunca ocupar a tela toda. Selecionável no editor (theme.heroStyle).
// Props:
//   personal   { firstName, lastName, title, bio }
//   images     { banner, headerBg }
//   eyebrow    string (theme.heroEyebrow)
//   actions    [{ label, url, variant: 'solid' | 'outline' }]
//   veilColor  hex da cor de fundo escura do tema (gradiente do véu)
//   nameColor / titleColor / bioColor  overrides opcionais
//   btnStyle   { bg, text } já resolvido (aceita degradê)
//   onShowQR   callback do botão de compartilhar
export default function HeroOverlay({
  personal = {},
  images = {},
  eyebrow = '',
  actions = [],
  veilColor = '#12100c',
  nameColor,
  titleColor,
  bioColor,
  btnStyle,
  onShowQR,
}) {
  const safeActions = Array.isArray(actions)
    ? actions.filter((a) => a && a.label && a.url).slice(0, 2)
    : [];
  const fullName =
    sanitizeText(`${personal.firstName || ''} ${personal.lastName || ''}`).trim() || 'Sem título';
  const bannerImg =
    images.banner ||
    (images.headerBg && images.headerBg.type === 'custom' ? images.headerBg.url : null);

  return (
    <header className="relative shrink-0 overflow-hidden">
      {/* imagem — altura fixa e limitada */}
      {bannerImg ? (
        <img
          src={bannerImg}
          alt=""
          className="block h-[52vh] min-h-[360px] max-h-[520px] w-full object-cover"
        />
      ) : (
        <div
          className="h-[52vh] min-h-[360px] max-h-[520px] w-full"
          style={{ background: `linear-gradient(160deg, ${veilColor}, #000)` }}
        />
      )}

      {/* véu */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `linear-gradient(to bottom, ${hexToRgba(veilColor, 0.25)} 0%, ${hexToRgba(veilColor, 0.72)} 50%, ${veilColor} 100%)`,
        }}
      />

      {/* botão compartilhar */}
      <button
        onClick={onShowQR}
        className="absolute top-4 right-4 z-10 bg-white/25 backdrop-blur-md p-2.5 rounded-full text-white hover:bg-white/40 transition-all border border-white/20 shadow-sm"
        aria-label="Mostrar QR code"
        title="Mostrar QR code"
      >
        <Share2 className="w-5 h-5" />
      </button>

      {/* conteúdo — sobreposto no rodapé */}
      <div className="absolute inset-x-0 bottom-0 px-6 pb-7 pt-14 sm:px-8">
        {eyebrow && (
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-action dark:text-action-dark mb-2">
            {sanitizeText(eyebrow)}
          </p>
        )}
        <h1
          className="text-4xl sm:text-5xl font-normal leading-[1.02] tracking-tight"
          style={{ fontFamily: 'var(--font-display, inherit)', color: nameColor || '#ffffff' }}
        >
          {fullName}
        </h1>
        {(personal.title || personal.bio) && (
          <p className="mt-2 text-sm max-w-md" style={{ color: bioColor || 'rgba(255,255,255,0.82)' }}>
            {personal.title && (
              <span className="italic" style={{ color: titleColor || '#ffffff' }}>
                {sanitizeText(personal.title)}.{' '}
              </span>
            )}
            {personal.bio && <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(personal.bio) }} />}
          </p>
        )}
        {safeActions.length > 0 && (
          <div className="mt-4 grid gap-2.5">
            {safeActions.map((a, i) => (
              <a
                key={i}
                href={sanitizeText(a.url)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-transform active:scale-[0.98]"
                style={
                  a.variant === 'outline'
                    ? { border: '1px solid rgba(255,255,255,0.28)', background: 'rgba(255,255,255,0.08)', color: '#ffffff', backdropFilter: 'blur(4px)' }
                    : { background: (btnStyle && btnStyle.bg) || 'var(--color-action-dark)', color: (btnStyle && btnStyle.text) || '#1a1410' }
                }
              >
                {a.variant !== 'outline' && <Play className="w-4 h-4 fill-current" />}
                {sanitizeText(a.label)}
              </a>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}
